import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import EventCard from "../components/EventCard";
import Loading from "../components/Loading";
import SearchBar from "../components/SearchBar";
import type { Event, Venue } from "../types/api";
import { CATEGORY_STYLES } from "../lib/visual";
import styles from "./SearchPage.module.css";

const CATEGORY_LABELS: Record<string, string> = {
  cat_concerts: "Concerts",
  cat_sports: "Sports",
  cat_theater: "Theater",
  cat_comedy: "Comedy",
  cat_family: "Family",
  cat_festivals: "Festivals",
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const category = params.get("category") ?? "";

  const [results, setResults] = useState<Event[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.searchEvents({
        q: q || undefined,
        category: category || undefined,
        limit: 60,
      }),
      api.recommendations(),
    ])
      .then(([res, recs]) => {
        setResults(res.events);
        setTotal(res.total);
        setVenues(recs.venues ?? []);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q, category]);

  function handleSearch(next: string) {
    const newParams = new URLSearchParams(params);
    if (next) newParams.set("q", next);
    else newParams.delete("q");
    setParams(newParams);
  }

  function clearCategory() {
    const newParams = new URLSearchParams(params);
    newParams.delete("category");
    setParams(newParams);
  }

  const venuesById = Object.fromEntries(venues.map((v) => [v.id, v]));
  const categoryLabel = category ? CATEGORY_LABELS[category] ?? category : null;
  const categoryStyle = category ? CATEGORY_STYLES[category] : undefined;

  let heading = "All events";
  if (q && categoryLabel) heading = `${categoryLabel} · "${q}"`;
  else if (q) heading = `"${q}"`;
  else if (categoryLabel) heading = categoryLabel;

  return (
    <div className={`container ${styles.page}`}>
      <header
        className={styles.head}
        style={
          categoryStyle
            ? { background: categoryStyle.gradient, color: "white" }
            : undefined
        }
      >
        <div className={styles.headBody}>
          <h1 className={styles.title}>{heading}</h1>
          <p className={styles.subtitle}>
            {loading
              ? "Searching..."
              : `${total} event${total === 1 ? "" : "s"} available`}
          </p>
        </div>
      </header>

      <div className={styles.controls}>
        <SearchBar initial={q} onSubmit={handleSearch} />
        {category && (
          <button className={styles.clearChip} onClick={clearCategory}>
            {categoryLabel} ✕
          </button>
        )}
      </div>

      {error && <div className="empty">Failed to search: {error}</div>}
      {loading && !error && <Loading />}
      {!loading && !error && results.length === 0 && (
        <div className="empty">No events match your search.</div>
      )}
      {!loading && results.length > 0 && (
        <div className={styles.grid}>
          {results.map((e) => (
            <EventCard
              key={e.id}
              event={e}
              venueName={venuesById[e.venueId]?.name}
              venueCity={venuesById[e.venueId]?.city}
              variant="wide"
            />
          ))}
        </div>
      )}
    </div>
  );
}
